import { Global, ThemeProvider, css } from '@emotion/react';
import React from 'react';

import { useBrandingStyles } from '~src/utils/branding-styles';

import { useGlobalState } from '../global-state';
import { colors } from './colors';

export interface ThemeModel {
  accent48: string;
  accent60: string;
  upsellColor: string;
}

export const WidgetTheme: React.FC<React.PropsWithChildren> = ({ children }) => {
  const globalState = useGlobalState();
  // TODO: move the config to global state.
  const session = globalState.sessions[0];

  const defaultTheme = {
    accent48: colors.black,
    accent60: colors.neutral40,
    upsellColor: colors.black,
  };

  const themeWithAccents = {
    accent48: session.flags.accent_color_1 ?? defaultTheme.accent48,
    accent60: session.flags.accent_color_2 ?? defaultTheme.accent60,
    /*
     * If no upsell_color is provided we fallback to accent_color_1
     */
    upsellColor:
      session.flags.upsell_color ?? session.flags.accent_color_1 ?? defaultTheme.upsellColor,
  };

  const theme = session.flags.enable_accent_color_for_widget_elements
    ? themeWithAccents
    : defaultTheme;

  return (
    <ThemeProvider theme={theme}>
      <Global
        styles={css`
          html {
            line-height: 1.15;
            -webkit-text-size-adjust: 100%;
          }
          main {
            display: block;
          }
          h1 {
            font-size: 2em;
            margin: 0.67em 0;
          }
          hr {
            box-sizing: content-box;
            height: 0;
            overflow: visible;
          }
          pre {
            font-family: monospace, monospace;
            font-size: 1em;
          }
          abbr[title] {
            border-bottom: none;
            text-decoration: underline;
            text-decoration: underline dotted;
          }
          b,
          strong {
            font-weight: bolder;
          }
          code,
          kbd,
          samp {
            font-family: monospace, monospace;
            font-size: 1em;
          }
          small {
            font-size: 80%;
          }
          sub,
          sup {
            font-size: 75%;
            line-height: 0;
            position: relative;
            vertical-align: baseline;
          }
          sub {
            bottom: -0.25em;
          }
          sup {
            top: -0.5em;
          }
          img {
            border-style: none;
          }
          button,
          input,
          optgroup,
          select,
          textarea {
            font-family: inherit;
            font-size: 100%;
            line-height: 1.15;
            margin: 0;
          }
          button,
          input {
            overflow: visible;
          }
          button,
          select {
            text-transform: none;
          }
          button,
          [type='button'],
          [type='reset'],
          [type='submit'] {
            -webkit-appearance: button;
          }
          button::-moz-focus-inner,
          [type='button']::-moz-focus-inner,
          [type='reset']::-moz-focus-inner,
          [type='submit']::-moz-focus-inner {
            border-style: none;
            padding: 0;
          }
          button:-moz-focusring,
          [type='button']:-moz-focusring,
          [type='reset']:-moz-focusring,
          [type='submit']:-moz-focusring {
            outline: 1px dotted ButtonText;
          }
          fieldset {
            padding: 0.35em 0.75em 0.625em;
          }
          legend {
            box-sizing: border-box;
            color: inherit;
            display: table;
            max-width: 100%;
            padding: 0;
            white-space: normal;
          }
          progress {
            vertical-align: baseline;
          }
          textarea {
            overflow: auto;
          }
          [type='checkbox'],
          [type='radio'] {
            box-sizing: border-box;
            padding: 0;
          }
          [type='number']::-webkit-inner-spin-button,
          [type='number']::-webkit-outer-spin-button {
            height: auto;
          }
          [type='search'] {
            -webkit-appearance: textfield;
            outline-offset: -2px;
          }
          [type='search']::-webkit-search-decoration {
            -webkit-appearance: none;
          }
          ::-webkit-file-upload-button {
            -webkit-appearance: button;
            font: inherit;
          }
          details {
            display: block;
          }
          summary {
            display: list-item;
          }
          template {
            display: none;
          }
          [hidden] {
            display: none;
          }

          body {
            width: 100vw;
            margin: 0;
            font-family: 'Inter var', 'Inter';
          }
          * {
            box-sizing: border-box;
            -webkit-tap-highlight-color: transparent;
          }

          button:focus {
            outline: none;
          }

          a {
            color: ${colors.black};
            text-decoration: underline;
            background-color: transparent;
          }
          a:hover {
            text-decoration: none;
          }
          a:focus {
            outline: none;
          }

          .tippy-box {
            background-color: ${colors.neutral35};
          }

          .tippy-arrow {
            color: ${colors.neutral35};
          }

          .sr-only {
            position: absolute;
            left: -10000px;
            top: auto;
            width: 1px;
            height: 1px;
            overflow: hidden;
          }

          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            margin: 0;
            font-size: 1px;
            font-weight: unset;
          }

          // focus-visible styles, there exist :focus-visible pseudoselector but not supported on some browsers
          // you know which browsers we are talking about so those styles are for polyfill
          .js-focus-visible :focus:not(.focus-visible) {
            outline: none;
          }
          .focus-visible {
            outline: none;
          }
          .focus-visible.focus-visible-outline {
            outline: 3px solid rgb(0, 160, 255);
          }
          .focus-visible.focus-visible-button {
            outline: 3px solid rgb(0, 160, 255);
            outline-offset: 2px;
          }
          .focus-visible.focus-visible-border {
            border: 1px dotted black;
          }
        `}
      />
      <WidgetBranding>{children}</WidgetBranding>
    </ThemeProvider>
  );
};

export const WidgetBranding: React.FC<React.PropsWithChildren> = ({ children }) => {
  useBrandingStyles();

  return children;
};
